import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { getApiHost, getQueryFetchOptions, parseHttpResponse } from '../utils';
import { ThemeOptions } from '../types';
import {
  DeliveryFulfillmentType,
  FulfillmentType,
  OrderAhead,
  Restaurant,
} from '../restaurant';

export const COMPANY_BASE_QUERY_KEY = 'company';

export const useCompanyQuery: (
  companyId: string,
  latitude?: string | null,
  longitude?: string | null,
  enabled?: boolean
) => UseQueryResult<Company, Error> = (
  companyId,
  latitude = null,
  longitude = null,
  enabled = true
) => {
  return useQuery<Company>({
    enabled: enabled && !!companyId,
    queryKey: [COMPANY_BASE_QUERY_KEY, companyId, latitude, longitude],
    queryFn: async () => {
      const apiHost = getApiHost();
      const url = new URL(`/api/company/${companyId}/restaurants`, apiHost);

      const params = new URLSearchParams();
      if (latitude && longitude)
        params.set('location', `${latitude},${longitude}`);
      url.search = params.toString();

      const response = await fetch(url, getQueryFetchOptions());

      return parseHttpResponse(response);
    },
  });
};

export type Company = {
  id: string;
  address: {
    country_alpha2: string | null;
  };
  android_app_url: string | null;
  delivery_only: boolean;
  has_catering_locations: boolean;
  ios_app_store_id: string | null;
  locations: CompanyLocation[];
  name: string;
  ordering_theme: ThemeOptions;
};

export type CompanyLocation = {
  fulfillment: {
    curbside: Pick<
      FulfillmentType,
      'is_available_now' | 'next_available_time'
    > | null;
    delivery: Pick<
      DeliveryFulfillmentType,
      'is_available_now' | 'next_available_time'
    > | null;
    pickup: Pick<
      FulfillmentType,
      'is_available_now' | 'next_available_time'
    > | null;
  };
  order_ahead: Pick<
    OrderAhead,
    'is_delivery_ahead_available' | 'is_pickup_ahead_available'
  > | null;
} & Pick<
  Restaurant,
  | 'id'
  | 'address'
  | 'allows_order_for_now'
  | 'company_id'
  | 'has_catering'
  | 'is_direct_live'
  | 'is_discover_live'
  | 'is_ordering_network_live'
  | 'name'
>;
